/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext, useRef } from "react";
import { FieldArray, Field, Form, Formik } from "formik";
import { GlobalLoaderContext, GlobalAuthContext } from "store/context";
import * as yup from "yup";
import Icon from "components/Icon";
import { GridColumnTitle, Label } from "components/Form";
import Row from "components/Grid/Row";
import Col from "components/Grid/Col";
import ComboBox from "components/Form/ComboBox3";
import InputField from "components/Form/InputField";
import Button from "components/Button";
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCollum,
  ViewText,
  ButtonContainer,
  ButtonContent,
  InlineLabel,
  LabelEdit
} from "../product_plan_person/styles";
import { FitinsurAPI, MessageCenter } from "utils";
import { coverageColumns } from "./columns";

const Coverages = ({  
  hasPermission,
  typeProductPlan,
  coverages,
  formOptions,
  productPlan,
  idProduct,
  onSaveCoverage
}) => {
  const formikRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const [validations, setValidations] = useState({});
  const [sortBy, setSortBy] = useState({ key: null, direction: null });
  const [addCoverage, setAddCoverage] = useState(false);
  const [idProductPlanCoverage, setIdProductPlanCoverage] = useState(false);
  const [idProductPlan, setIdProductPlan] = useState(null);
  const [idProductCoverage, setIdProductCoverage] = useState(null);
  const [idCoverage, setIdCoverage] = useState(null);
  const [initial, setInitial] = useState({
    coverage: {
      label: "",
      value: ""
    },
    coverageType: {
      idDomain: "",
      domainDescription: "",
      idDomainType: "",
    },
    premiumNet: "",
    limit: "",
    rate: "",
    deductible: "",
    taxRate: "",
    maxPeriod: "",
    maxLimitClaimRate: "",
    deductibleDescription: ""
  });

  const resetForm = () => {
    formikRef.current.values.coverage = '';
    formikRef.current.values.coverageType = '';
    formikRef.current.values.premiumNet = '';
    formikRef.current.values.limit = '';
    formikRef.current.values.rate = '';
    formikRef.current.values.deductible = '';
    formikRef.current.values.taxRate = '';
    formikRef.current.values.maxPeriod = '';
    formikRef.current.values.maxLimitClaimRate = '';
    formikRef.current.values.deductibleDescription = '';
  };

  const isCalculation = typeProductPlan.idDomain === 12;
  const columnWidth = isCalculation ? 3 : 4;
  
  const createValidation = () => {
    let validations = yup.object().shape({
      coverage: yup.string().required("* Required field"),
      coverageType: yup.string().required("* Required field"),
      premiumNet: isCalculation ? yup.string().required("* Required field") : null,
      limit: yup.string().nullable().required("* Required field"),
      rate: yup.string().nullable().notRequired(),
      deductible: yup.string().nullable().notRequired(),
      taxRate: yup.string().nullable().notRequired(),
      maxPeriod: yup.string().nullable().notRequired(),
      maxLimitClaimRate: yup.string().nullable().notRequired(),
      deductibleDescription: yup.string().notRequired(),
    });
    setValidations(validations);
    return validations;
  };

  useEffect(() => {
    createValidation();
  }, []);

  const setEditCoverage = (coverage) => {
    formikRef.current.values.coverage = coverage.idCoverage;
    formikRef.current.values.coverageType = { idDomain: coverage.coverageType.idDomain, description: coverage.coverageType.description, idDomainType: coverage.coverageType.idDomainType };
    formikRef.current.values.premiumNet = coverage.premiumNet;
    formikRef.current.values.limit = coverage.maxLimitClaim;
    formikRef.current.values.rate = coverage.rate;
    formikRef.current.values.deductible = coverage.deductible;
    formikRef.current.values.taxRate = coverage.taxRate;
    formikRef.current.values.maxPeriod = coverage.maxPeriod;
    formikRef.current.values.maxLimitClaimRate = coverage.maxLimitClaimRate;
    formikRef.current.values.deductibleDescription = coverage.deductibleDescription;

    
    setIdProductPlanCoverage(coverage.idProductPlanCoverage);
    setIdProductPlan(coverage.idProductPlan);
    setIdProductCoverage(coverage.idProductCoverage);  
    setAddCoverage(true);
  }

  const getFiledOptions = function (answerOptions) {
    return answerOptions.map(option => {
        return {
          label: option.description,
          value: {
            idDomain: option.idDomain,
            description: option.description,
            idDomainType: option.idDomainType,
          }
        };
    });
  }
  const saveCoverage = async (body) => {
    try {
      loading.start();
      body.idProductPlanCoverage = idProductPlanCoverage;
      body.idProductPlan = idProductPlan || productPlan;
      body.idProductCoverage = idProductCoverage;
      body.idCoverage = idCoverage;
      body.isCalculation = isCalculation;
      body.idProduct = idProduct;

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestSaveCoverage(body).then(res => res.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      setIdProductPlanCoverage(null);
      setAddCoverage(false);
      resetForm();
      onSaveCoverage && onSaveCoverage();
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initial}
      enableReinitialize={true}
      onSubmit={values => saveCoverage(values)}
      validationSchema={validations}
    >
      {({ setFieldValue, values }) => (
        <>
          <Table>
            <TableHead>
              <TableRow>
                {coverageColumns.map((column, index) => (
                  <TableHeader
                    right={column.right}
                    key={index}
                    minWidth={column.minWidth}
                    maxWidth={column.maxWidth}
                  >
                    <GridColumnTitle
                      style={{
                        cursor: column.value ? "pointer" : "not-allowed",
                      }}
                    >
                      {column.label}
                    </GridColumnTitle>
                    {column.value &&
                    sortBy.key === column.value &&
                    sortBy.direction === "asc" ? (
                      <Icon name={"arrowup"} />
                    ) : column.value &&
                      sortBy.key === column.value &&
                      sortBy.direction === "desc" ? (
                      <Icon name={"arrowdown"} />
                    ) : (
                      ""
                    )}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <FieldArray
                name="coverages"
                render={() => (
                  coverages && coverages.map((coverage, index) => (
                  <>
                    <TableRow>
                      <TableCollum>
                        <InlineLabel>
                          Coverage
                        </InlineLabel>
                        <LabelEdit fontSize={12}>
                          {coverage.coverageName.description}
                        </LabelEdit>
                      </TableCollum>

                      <TableCollum>
                        <InlineLabel>
                          Coverage Type
                        </InlineLabel>
                        <LabelEdit fontSize={12}>
                          {coverage.coverageType.description}
                        </LabelEdit>
                      </TableCollum>

                      <TableCollum>
                        <InlineLabel>
                          Premium Net
                        </InlineLabel>
                        <LabelEdit fontSize={12}>
                        {coverage.premiumNet || ""}
                        </LabelEdit>
                      </TableCollum>

                      <TableCollum>
                        <InlineLabel>
                          Limit
                        </InlineLabel>
                        <LabelEdit fontSize={12}>
                         {coverage.maxLimitClaim || ""}
                        </LabelEdit>
                      </TableCollum>

                      <TableCollum>
                        <InlineLabel>
                          Deductible
                        </InlineLabel>
                        <LabelEdit fontSize={12}>
                          {coverage.deductible || ""}
                        </LabelEdit>
                      </TableCollum>

                      <TableCollum>
                        <InlineLabel>
                          Rate
                        </InlineLabel>
                        <LabelEdit fontSize={12}>
                          {coverage.rate || ""}
                        </LabelEdit>
                      </TableCollum>

                      <TableCollum>
                        <InlineLabel>
                          Tax Rate
                        </InlineLabel>
                        <LabelEdit fontSize={12}>
                          {coverage.taxRate}
                        </LabelEdit>
                      </TableCollum>

                      <TableCollum>
                        <InlineLabel>
                          Max. Period
                        </InlineLabel>
                        <LabelEdit fontSize={12}>
                          {coverage.maxPeriod || ""}
                        </LabelEdit>
                      </TableCollum>

                      <TableCollum>
                        <InlineLabel>
                          Deductible Description
                        </InlineLabel>
                        <LabelEdit fontSize={12}>
                          {coverage.deductibleDescription}
                        </LabelEdit>
                      </TableCollum>

                      <TableCollum>
                        <InlineLabel>
                         Max. Lim. Claim Rate
                        </InlineLabel>
                        <LabelEdit fontSize={12}>
                          {coverage.maxLimitClaimRate || ""}
                        </LabelEdit>
                      </TableCollum>

                      <TableCollum right>
                        <InlineLabel>ACTION</InlineLabel>
                        <Label>
                          <Icon
                            name="edit"
                            style={{ alignSelf: "center", cursor: "pointer" }}
                            onClick={() => setEditCoverage(coverage)}
                          />
                        </Label>
                      </TableCollum>
                    </TableRow>
                  </>
                  ))
                )}
              />
            </TableBody>
          </Table>

          {!addCoverage && (
            <Row>
              <Col lg={6} md={6} sm={6} xs={12}></Col>
              <Col lg={6} md={6} sm={6} xs={12}>
                <ViewText style={{ marginTop: "15px" }}>
                  <Icon
                    name="plus"
                    onClick={() => {setAddCoverage(true) }}
                    style={{ cursor: "pointer" }}
                  />
                </ViewText>
              </Col>
            </Row>
          )}

          {addCoverage && (
            <Form>
              <div style={{ marginTop: 40, marginBottom: 10 }}>
                <Row>
                  <Col lg={columnWidth} md={columnWidth} sm={columnWidth} xs={12}>
                    <Field
                      name="coverage"
                      label="Coverage"
                      isRequired
                      options={formOptions.coveragesByProduct || []}
                      onChange={(e) => {
                        setFieldValue(`coverage`, e)
                      }}
                      component={ComboBox}
                      disabled={!hasPermission}
                    />
                  </Col>
                  <Col lg={columnWidth} md={columnWidth} sm={columnWidth} xs={12}>
                    <Field
                      name="coverageType"
                      label="Coverage Type"
                      isRequired={true}
                      options={getFiledOptions(formOptions.coverageTypesByProduct) || []}
                      searchList={formOptions.coverageTypesByProduct || []}
                      onChange={(e) => {
                        setFieldValue(`coverageType`, e);
                      }}
                      component={ComboBox}
                      disabled={!hasPermission}
                    />
                  </Col>
                  
                  {isCalculation &&
                    <Col lg={columnWidth} md={columnWidth} sm={columnWidth} xs={12}>
                      <Field
                        name="premiumNet"
                        label="Premium Net"
                        component={InputField}
                        type="Number"
                        disabled={!hasPermission}
                        value={values.premiumNet}
                        isRequired={isCalculation ? true : false}
                      />
                    </Col>
                  }

                  <Col lg={columnWidth} md={columnWidth} sm={columnWidth} xs={12}>
                    <Field
                      name="limit"
                      label="Limit"
                      component={InputField}
                      isRequired
                      type="Number"
                      disabled={!hasPermission}
                      value={values.limit}
                    />
                  </Col>
                </Row>


                <Row>
                  {isCalculation ?
                    <Col lg={3} md={3} sm={3} xs={12}>
                      <Field
                        name="rate"
                        label="Rate"
                        component={InputField}
                        disabled={!hasPermission}
                        type="Number"
                        value={values.rate}
                      />
                    </Col>
                    : 
                    <Col lg={3} md={3} sm={3} xs={12}>
                      <Field
                        name="deductible"
                        label="Deductible"
                        component={InputField}
                        disabled={!hasPermission}
                        type="Number"
                        value={values.deductible}
                      />
                    </Col>
                  }

                  <Col lg={3} md={3} sm={3} xs={12}>
                    <Field
                      name="taxRate"
                      label="Tax Rate"
                      component={InputField}
                      disabled={!hasPermission}
                      type="Number"
                      value={values.taxRate}
                    />
                  </Col>
                  <Col lg={3} md={3} sm={3} xs={12}>
                    <Field
                      name="maxPeriod"
                      label="Max. Period(days)"
                      component={InputField}
                      disabled={!hasPermission}
                      type="Number"
                      value={values.maxPeriod}
                    />
                  </Col>
                  <Col lg={3} md={3} sm={3} xs={12}>
                    <Field
                      name="maxLimitClaimRate"
                      label="Max Limit Claim Rate"
                      component={InputField}
                      disabled={!hasPermission}
                      type="Number"
                      value={values.maxLimitClaimRate}
                    />
                  </Col>
                </Row>
                {isCalculation && 
                  <Row>
                    <Col lg={12} md={12} sm={12} xs={12}>
                      <Field
                        name="deductibleDescription"
                        label="Deductible Description"
                        component={InputField}
                        disabled={!hasPermission}
                        value={values.deductibleDescription}
                      />
                    </Col>
                  </Row>
                }

                <ButtonContainer>
                  <ButtonContainer
                    style={{
                      justifyContent: "flex-start",
                      alignItems: "center",
                      paddingBottom: "10px",
                    }}
                  />
                  <ButtonContent>
                    <Button
                      fullWidth
                      style={{ marginLeft: 15 }}
                      third
                      onClick={() => {
                        setIdProductPlanCoverage(null);
                        setAddCoverage(false);
                        resetForm();
                      }}
                    >
                      CANCEL
                    </Button>
                  </ButtonContent>
                  <ButtonContent>
                    <Button
                      fullWidth
                      style={{ marginLeft: 15 }}
                      type="submit"
                      primary
                      onClick={() => {
                        createValidation()
                          .validate(values)
                          .catch((e) => {
                            console.log("validation error", e);
                            mc.error("Please fill in all required fields.");
                          });
                      }}
                      id="btnSubmit"
                    >
                      {idProductPlanCoverage ? "SAVE COVERAGE" : "ADD COVERAGE"}
                    </Button>
                  </ButtonContent>
                </ButtonContainer>
              </div>
            </Form>
          )}
        </>
      )}
    </Formik>
  );
};

export default Coverages;
